import type { PageHelp } from '../../help/types';

/** Page guide for `/settings/users` (spec §9.5, §6.3). */
export const usersHelp: PageHelp = {
  title: 'Users',
  intro:
    'Who may sign in at all, and what each person may do. A signed-in Entra user without a row here is refused by the API, so registering somebody is the first step of onboarding them — before they report that nothing works.',
  sections: ['Finding people', 'Roles and access', 'Register user', 'Grants'],
  contexts: {
    'register-dialog': 'Click “Register user” at the top right.',
    'grants-dialog': 'Click “Grants” at the end of a person’s row.',
  },
  tips: [
    'Most people need grants, not global roles: a grant gives a role on one project or package, optionally narrowed to some locales, and a person without any grant sees no project at all.',
  ],
  steps: [
    // --- Finding people --------------------------------------------------------------------
    {
      selector: '[data-help="users-search"]',
      title: 'Find a person',
      body: 'Filters the list as you type, by display name or email. Any part of the name or address matches, regardless of upper or lower case.',
      section: 'Finding people',
      i18n: {
        'de-AT': {
          title: 'Person finden',
          body: 'Filtert die Liste während der Eingabe nach Anzeigename oder E-Mail. Jeder Teil des Namens oder der Adresse passt, unabhängig von Groß- und Kleinschreibung.',
        },
        'sr-Latn-RS': {
          title: 'Pronađite osobu',
          body: 'Filtrira listu dok kucate, po imenu za prikaz ili e-adresi. Poklapa se bilo koji deo imena ili adrese, bez obzira na velika i mala slova.',
        },
      },
    },
    {
      selector: '[data-help="users-list"]',
      title: 'Read the user list',
      body: 'Everyone who may sign in, sorted by email, with their global roles, their grants on projects and packages, and whether the account is active. Changes to roles and the active switch are saved as soon as you make them.',
      section: 'Finding people',
      i18n: {
        'de-AT': {
          title: 'Benutzerliste lesen',
          body: 'Alle, die sich anmelden dürfen, nach E-Mail sortiert, mit ihren globalen Rollen, ihren Berechtigungen auf Projekte und Pakete und der Angabe, ob das Konto aktiv ist. Änderungen an Rollen und am Aktiv-Schalter werden sofort gespeichert.',
        },
        'sr-Latn-RS': {
          title: 'Pregledajte listu korisnika',
          body: 'Svi koji smeju da se prijave, poređani po e-adresi, sa globalnim ulogama, dozvolama na projektima i paketima i podatkom da li je nalog aktivan. Izmene uloga i prekidača za aktivnost čuvaju se odmah.',
        },
      },
    },
    {
      selector: '[data-help="users-person"]',
      title: 'Recognise a person',
      body: 'Display name and work email. “bound at first sign-in” means the person has not signed in yet: the account is matched by email and tied to their Entra object id the first time they do.',
      section: 'Finding people',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Person erkennen',
          body: 'Anzeigename und dienstliche E-Mail. „bound at first sign-in“ heißt, die Person hat sich noch nicht angemeldet: Das Konto wird über die E-Mail zugeordnet und bei der ersten Anmeldung an ihre Entra-Objekt-ID gebunden.',
        },
        'sr-Latn-RS': {
          title: 'Prepoznajte osobu',
          body: 'Ime za prikaz i službena e-adresa. „bound at first sign-in“ znači da se osoba još nije prijavila: nalog se prepoznaje po e-adresi i vezuje za njen Entra object id pri prvoj prijavi.',
        },
      },
    },

    // --- Roles and access ------------------------------------------------------------------
    {
      selector: '[data-help="users-global-roles"]',
      title: 'Change global roles',
      body: 'Saved as soon as you change the selection. admin and superadmin open the Administration pages and every project and package. The global translator, developer and viewer roles only stand in for a missing grant when the server runs in permissive authorization mode, so most people need grants instead. Nobody can remove their own superadmin role.',
      section: 'Roles and access',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Globale Rollen ändern',
          body: 'Wird gespeichert, sobald Sie die Auswahl ändern. admin und superadmin öffnen die Administrationsseiten sowie jedes Projekt und Paket. Die globalen Rollen translator, developer und viewer ersetzen eine fehlende Berechtigung nur, wenn der Server im permissiven Autorisierungsmodus läuft – die meisten Personen brauchen daher Berechtigungen. Die eigene superadmin-Rolle kann niemand entfernen.',
        },
        'sr-Latn-RS': {
          title: 'Promenite globalne uloge',
          body: 'Čuva se čim promenite izbor. admin i superadmin otvaraju administratorske stranice i svaki projekat i paket. Globalne uloge translator, developer i viewer zamenjuju dozvolu koja nedostaje samo kada server radi u permisivnom režimu autorizacije, pa je većini ljudi potrebna dozvola. Niko ne može sebi da ukloni superadmin ulogu.',
        },
      },
    },
    {
      selector: '[data-help="users-scope-grants"]',
      title: 'Read the scope grants',
      body: 'Each line is one grant: the project or package code, the role on it and, in brackets, the locales it is narrowed to. A grant without brackets covers every locale of that scope.',
      section: 'Roles and access',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Berechtigungen lesen',
          body: 'Jede Zeile ist eine Berechtigung: der Code des Projekts oder Pakets, die Rolle darauf und in Klammern die Locales, auf die sie eingeschränkt ist. Eine Berechtigung ohne Klammern gilt für jede Locale dieses Bereichs.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte dozvole',
          body: 'Svaki red je jedna dozvola: kod projekta ili paketa, uloga na njemu i, u zagradi, locale-i na koje je ograničena. Dozvola bez zagrade važi za svaki locale tog opsega.',
        },
      },
    },
    {
      selector: '[data-help="users-active"]',
      title: 'Deactivate an account',
      body: 'Switching it off takes effect on the person’s next request: the API refuses an inactive account just like an unregistered one, while the row and its grants stay for later. You cannot deactivate your own account.',
      section: 'Roles and access',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Konto deaktivieren',
          body: 'Das Ausschalten wirkt ab der nächsten Anfrage der Person: Die API lehnt ein inaktives Konto genauso ab wie ein nicht registriertes, Zeile und Berechtigungen bleiben aber für später erhalten. Ihr eigenes Konto können Sie nicht deaktivieren.',
        },
        'sr-Latn-RS': {
          title: 'Deaktivirajte nalog',
          body: 'Isključivanje važi od sledećeg zahteva te osobe: API odbija neaktivan nalog isto kao neregistrovan, a red i dozvole ostaju za kasnije. Sopstveni nalog ne možete da deaktivirate.',
        },
      },
    },
    {
      selector: '[data-help="users-grants"]',
      title: 'Edit a person’s grants',
      body: 'Opens the grants of this person: one line per project or package, with a role and an optional locale list. The list you save replaces all of their grants at once.',
      section: 'Roles and access',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Berechtigungen einer Person bearbeiten',
          body: 'Öffnet die Berechtigungen dieser Person: eine Zeile pro Projekt oder Paket, mit Rolle und optionaler Locale-Liste. Die gespeicherte Liste ersetzt alle ihre Berechtigungen auf einmal.',
        },
        'sr-Latn-RS': {
          title: 'Izmenite dozvole osobe',
          body: 'Otvara dozvole ove osobe: po jedan red za projekat ili paket, sa ulogom i opcionom listom locale-a. Sačuvana lista odjednom zamenjuje sve njene dozvole.',
        },
      },
    },

    // --- Register user ---------------------------------------------------------------------
    {
      selector: '[data-help="users-create"]',
      title: 'Register somebody',
      body: 'Opens the “Register user” dialog. A signed-in Entra user with no row on this list is refused by the API with no exemptions, so register people before they first try to sign in.',
      section: 'Register user',
      i18n: {
        'de-AT': {
          title: 'Jemanden registrieren',
          body: 'Öffnet den Dialog „Register user“. Ein angemeldeter Entra-Benutzer ohne Zeile in dieser Liste wird von der API ausnahmslos abgelehnt – registrieren Sie Personen also, bevor sie sich zum ersten Mal anmelden.',
        },
        'sr-Latn-RS': {
          title: 'Registrujte nekoga',
          body: 'Otvara dijalog „Register user“. Prijavljenog Entra korisnika bez reda na ovoj listi API odbija bez izuzetka, pa ljude registrujte pre nego što prvi put pokušaju da se prijave.',
        },
      },
    },
    {
      selector: '[data-help="users-register-email"]',
      title: 'Enter the work email',
      body: 'The email is enough: the Entra object id is bound the first time the person signs in. An email that is already registered is refused, and “Register” stays disabled until the field contains an @.',
      section: 'Register user',
      context: 'register-dialog',
      i18n: {
        'de-AT': {
          title: 'Dienstliche E-Mail eingeben',
          body: 'Die E-Mail genügt: Die Entra-Objekt-ID wird bei der ersten Anmeldung gebunden. Eine bereits registrierte E-Mail wird abgelehnt, und „Register“ bleibt inaktiv, bis das Feld ein @ enthält.',
        },
        'sr-Latn-RS': {
          title: 'Unesite službenu e-adresu',
          body: 'E-adresa je dovoljna: Entra object id se vezuje pri prvoj prijavi. Već registrovana e-adresa se odbija, a „Register“ ostaje neaktivno dok polje ne sadrži @.',
        },
      },
    },
    {
      selector: '[data-help="users-register-name"]',
      title: 'Add a display name',
      body: 'Optional. When set, the user list shows it above the email, and every history in the app — imports, releases, revisions, suggestions, activity — names the person with it. Left empty, it is taken from Entra at the first sign-in; a name typed here is never replaced.',
      section: 'Register user',
      context: 'register-dialog',
      i18n: {
        'de-AT': {
          title: 'Anzeigenamen ergänzen',
          body: 'Optional. Wenn gesetzt, zeigt die Benutzerliste ihn über der E-Mail, und jeder Verlauf in der App – Importe, Releases, Revisionen, Vorschläge, Aktivität – nennt die Person damit. Bleibt das Feld leer, wird der Name bei der ersten Anmeldung aus Entra übernommen; ein hier eingegebener Name wird nie ersetzt.',
        },
        'sr-Latn-RS': {
          title: 'Dodajte ime za prikaz',
          body: 'Opciono. Kada je uneto, lista korisnika ga prikazuje iznad e-adrese, a svaka istorija u aplikaciji – uvozi, izdanja, revizije, predlozi, aktivnost – navodi osobu tim imenom. Ako ostane prazno, preuzima se iz Entra-e pri prvoj prijavi; ime uneto ovde se nikad ne zamenjuje.',
        },
      },
    },
    {
      selector: '[data-help="users-register-roles"]',
      title: 'Give global roles only when needed',
      body: 'Optional. Leave it empty for most people and give them grants on their projects afterwards; pick admin only for somebody who has to manage users, API tokens and locales.',
      section: 'Register user',
      context: 'register-dialog',
      i18n: {
        'de-AT': {
          title: 'Globale Rollen nur bei Bedarf vergeben',
          body: 'Optional. Lassen Sie das Feld bei den meisten Personen leer und vergeben Sie danach Berechtigungen auf ihre Projekte; wählen Sie admin nur für jemanden, der Benutzer, API-Tokens und Locales verwalten muss.',
        },
        'sr-Latn-RS': {
          title: 'Globalne uloge dajte samo kad su potrebne',
          body: 'Opciono. Za većinu ljudi ostavite prazno i posle im dodelite dozvole na njihovim projektima; admin izaberite samo za nekoga ko mora da upravlja korisnicima, API tokenima i locale-ima.',
        },
      },
    },
    {
      selector: '[data-help="users-register-submit"]',
      title: 'Register the user',
      body: 'Creates the account, and the person can sign in right away. Without a grant (or an admin role) they see no project at all, so open “Grants” on their row next.',
      section: 'Register user',
      context: 'register-dialog',
      i18n: {
        'de-AT': {
          title: 'Benutzer registrieren',
          body: 'Legt das Konto an, die Person kann sich sofort anmelden. Ohne Berechtigung (oder admin-Rolle) sieht sie aber kein einziges Projekt – öffnen Sie also als Nächstes „Grants“ in ihrer Zeile.',
        },
        'sr-Latn-RS': {
          title: 'Registrujte korisnika',
          body: 'Pravi nalog i osoba može odmah da se prijavi. Bez dozvole (ili admin uloge) ne vidi nijedan projekat, pa zatim otvorite „Grants“ u njenom redu.',
        },
      },
    },

    // --- Grants ----------------------------------------------------------------------------
    {
      selector: '[data-help="users-grant-scope"]',
      title: 'Pick the project or package',
      body: 'Which project or package this grant is for. Every project and package is offered, and a person may hold grants on as many of them as needed.',
      section: 'Grants',
      context: 'grants-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Projekt oder Paket wählen',
          body: 'Für welches Projekt oder Paket diese Berechtigung gilt. Angeboten wird jedes Projekt und Paket, und eine Person kann auf beliebig viele davon Berechtigungen haben.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite projekat ili paket',
          body: 'Za koji projekat ili paket važi ova dozvola. Nudi se svaki projekat i paket, a osoba može imati dozvole na koliko god ih je potrebno.',
        },
      },
    },
    {
      selector: '[data-help="users-grant-role"]',
      title: 'Pick the role',
      body: 'owner may do everything in the scope, including its settings, production publishing and rollback; translator edits and reviews translations; developer manages keys and missing keys; viewer only reads. Every role can see the scope.',
      section: 'Grants',
      context: 'grants-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Rolle wählen',
          body: 'owner darf im Bereich alles, auch Einstellungen, Veröffentlichung in Produktion und Rollback; translator bearbeitet und reviewt Übersetzungen; developer verwaltet Keys und fehlende Keys; viewer liest nur. Jede Rolle sieht den Bereich.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite ulogu',
          body: 'owner sme sve u opsegu, uključujući podešavanja, objavljivanje u produkciju i rollback; translator menja i odobrava prevode; developer upravlja ključevima i ključevima koji nedostaju; viewer samo čita. Svaka uloga vidi opseg.',
        },
      },
    },
    {
      selector: '[data-help="users-grant-locales"]',
      title: 'Narrow a grant to some locales',
      body: 'Comma-separated locale codes such as `de-AT, sr-Latn-RS`; empty means every locale of the scope. A narrowed grant limits the locales the person may write — editing or importing any other is refused — unless they also hold an unrestricted grant on the same scope. Codes are stored in their correct spelling (an alias such as `sr-RS-Latn` is saved as `sr-Latn-RS`), and an unknown code is refused when you save.',
      section: 'Grants',
      context: 'grants-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Berechtigung auf Locales einschränken',
          body: 'Durch Beistriche getrennte Locale-Codes wie `de-AT, sr-Latn-RS`; leer heißt jede Locale des Bereichs. Eine eingeschränkte Berechtigung begrenzt die Locales, in die die Person schreiben darf – Bearbeiten oder Importieren in einer anderen wird abgelehnt –, außer sie hat im selben Bereich auch eine uneingeschränkte Berechtigung. Codes werden in ihrer korrekten Schreibweise gespeichert (ein Alias wie `sr-RS-Latn` als `sr-Latn-RS`), ein unbekannter Code wird beim Speichern abgelehnt.',
        },
        'sr-Latn-RS': {
          title: 'Ograničite dozvolu na pojedine locale-e',
          body: 'Kodovi locale-a odvojeni zarezom, na primer `de-AT, sr-Latn-RS`; prazno znači svaki locale opsega. Ograničena dozvola sužava locale-e u koje osoba sme da upisuje – izmena ili uvoz u bilo kom drugom se odbija – osim ako na istom opsegu ima i neograničenu dozvolu. Kodovi se čuvaju u ispravnom zapisu (alias kao `sr-RS-Latn` čuva se kao `sr-Latn-RS`), a nepoznat kod se odbija pri čuvanju.',
        },
      },
    },
    {
      selector: '[data-help="users-grant-remove"]',
      title: 'Remove a grant',
      body: 'Takes the line out of the list in the dialog. Nothing changes for the person until you click “Save”.',
      section: 'Grants',
      context: 'grants-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Berechtigung entfernen',
          body: 'Nimmt die Zeile aus der Liste im Dialog. Für die Person ändert sich nichts, bis Sie auf „Save“ klicken.',
        },
        'sr-Latn-RS': {
          title: 'Uklonite dozvolu',
          body: 'Uklanja red sa liste u dijalogu. Za osobu se ništa ne menja dok ne kliknete na „Save“.',
        },
      },
    },
    {
      selector: '[data-help="users-grant-add"]',
      title: 'Add a grant',
      body: 'Adds a line for the first project in the list with the role viewer; change the scope and the role before saving. Disabled while there is no project or package to grant.',
      section: 'Grants',
      context: 'grants-dialog',
      i18n: {
        'de-AT': {
          title: 'Berechtigung hinzufügen',
          body: 'Fügt eine Zeile für das erste Projekt der Liste mit der Rolle viewer hinzu; ändern Sie Bereich und Rolle vor dem Speichern. Inaktiv, solange es kein Projekt und kein Paket gibt.',
        },
        'sr-Latn-RS': {
          title: 'Dodajte dozvolu',
          body: 'Dodaje red za prvi projekat sa liste sa ulogom viewer; pre čuvanja promenite opseg i ulogu. Neaktivno dok ne postoji nijedan projekat ni paket.',
        },
      },
    },
    {
      selector: '[data-help="users-grants-duplicate"]',
      title: 'Resolve a duplicated grant',
      body: 'Appears when two lines give the same role on the same project or package — easy to cause with “Add grant”, which starts as viewer on the first project. “Save” stays disabled until you change the scope or role of one line, or remove it.',
      section: 'Grants',
      context: 'grants-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Doppelte Berechtigung auflösen',
          body: 'Erscheint, wenn zwei Zeilen dieselbe Rolle auf dasselbe Projekt oder Paket vergeben – leicht passiert mit „Add grant“, das mit viewer auf dem ersten Projekt beginnt. „Save“ bleibt inaktiv, bis Sie Bereich oder Rolle einer Zeile ändern oder sie entfernen.',
        },
        'sr-Latn-RS': {
          title: 'Rešite dupliranu dozvolu',
          body: 'Pojavljuje se kada dva reda daju istu ulogu na istom projektu ili paketu – lako se desi preko „Add grant“, koje počinje sa viewer na prvom projektu. „Save“ ostaje neaktivno dok ne promenite opseg ili ulogu jednog reda, ili ga ne uklonite.',
        },
      },
    },
    {
      selector: '[data-help="users-grants-save"]',
      title: 'Save the grants',
      body: 'Replaces all of this person’s grants with the lines in the dialog, as one set. “Cancel” discards every change made in the dialog.',
      section: 'Grants',
      context: 'grants-dialog',
      i18n: {
        'de-AT': {
          title: 'Berechtigungen speichern',
          body: 'Ersetzt alle Berechtigungen dieser Person in einem Schritt durch die Zeilen im Dialog. „Cancel“ verwirft jede Änderung im Dialog.',
        },
        'sr-Latn-RS': {
          title: 'Sačuvajte dozvole',
          body: 'Zamenjuje sve dozvole ove osobe redovima iz dijaloga, odjednom. „Cancel“ odbacuje svaku izmenu napravljenu u dijalogu.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Benutzer',
      intro:
        'Wer sich überhaupt anmelden darf und was jede Person tun darf. Ein angemeldeter Entra-Benutzer ohne Zeile hier wird von der API abgelehnt – die Registrierung ist also der erste Schritt beim Onboarding, noch bevor jemand meldet, dass nichts funktioniert.',
      sections: {
        'Finding people': 'Personen finden',
        'Roles and access': 'Rollen und Zugriff',
        'Register user': 'Benutzer registrieren',
        Grants: 'Berechtigungen',
      },
      contexts: {
        'register-dialog': 'Klicken Sie rechts oben auf „Register user“.',
        'grants-dialog': 'Klicken Sie am Ende der Zeile einer Person auf „Grants“.',
      },
      tips: [
        'Die meisten Personen brauchen Berechtigungen, keine globalen Rollen: Eine Berechtigung vergibt eine Rolle auf ein Projekt oder Paket, optional auf einige Locales eingeschränkt, und wer keine Berechtigung hat, sieht kein einziges Projekt.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Korisnici',
      intro:
        'Ko uopšte sme da se prijavi i šta svaka osoba sme da radi. Prijavljenog Entra korisnika bez reda ovde API odbija, pa je registracija prvi korak uvođenja nove osobe – pre nego što javi da ništa ne radi.',
      sections: {
        'Finding people': 'Pronalaženje ljudi',
        'Roles and access': 'Uloge i pristup',
        'Register user': 'Registracija korisnika',
        Grants: 'Dozvole',
      },
      contexts: {
        'register-dialog': 'Kliknite na „Register user“ gore desno.',
        'grants-dialog': 'Kliknite na „Grants“ na kraju reda osobe.',
      },
      tips: [
        'Većini ljudi trebaju dozvole, a ne globalne uloge: dozvola daje ulogu na jednom projektu ili paketu, po potrebi ograničenu na neke locale-e, a osoba bez ijedne dozvole ne vidi nijedan projekat.',
      ],
    },
  },
};
